import { useSuspenseQuery, useMutation, useQueryClient, useIsMutating, useMutationState } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo } from 'react'
import { ChatMessage, ChatRequest, ChatResponse, LiveChatDataSchema } from './types'
import {
  CHAT_HANDLER_ID_STORAGE_KEY,
  CHAT_MESSAGES_STORAGE_KEY,
  fetchChatHistory,
  getChatSchema,
  getLiveChatSchema,
  getExistingLiveChatSession,
  startLiveChatSession,
  saveMessagesToStorage,
  sendChatMessage,
  clearLiveChatSessionData,
} from './utils'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { useIsPageBuilder } from '@/catalog-apps/hooks/useIsPageBuilder'

// Custom hooks

export const useGetChatSchema = ({ contentId }: { contentId: string }) => {
  const isPageBuilder = useIsPageBuilder()
  const { appKey, projectId } = useAppContext()

  return useSuspenseQuery({
    queryKey: ['chat-schema'],
    queryFn: () => getChatSchema({ appKey, contentId, projectId, isPageBuilder }),
    staleTime: Infinity,
  })
}

/**
 * Hook to fetch LiveChat UI schema and session data from backend.
 * Backend checks Redis for existing session based on user context.
 */
export const useGetLiveChatSchema = ({ contentId }: { contentId: string }) => {
  const isPageBuilder = useIsPageBuilder()
  const { appKey, projectId } = useAppContext()

  return useSuspenseQuery({
    queryKey: ['live-chat-schema', contentId],
    queryFn: () => getLiveChatSchema({ appKey, contentId, projectId, isPageBuilder }),
    staleTime: 0, // Always refetch to check Redis for session state
  })
}

/**
 * Hook to start a live chat session.
 * This calls the event handler to create a new worker task and returns connection details.
 */
export const useStartLiveChatSession = ({ contentId }: { contentId: string }) => {
  const { appKey, projectId } = useAppContext()

  return useMutation({
    mutationKey: ['live-chat-start', contentId],
    mutationFn: async ({ handlerId }: { handlerId: string }): Promise<LiveChatDataSchema> => {
      if (!appKey || !projectId) {
        throw new Error('Missing app context')
      }

      // Check for existing session to support reconnection
      const existingSession = getExistingLiveChatSession(contentId)

      return startLiveChatSession({
        appKey,
        handlerId,
        projectId,
        contentId,
        existingRequestId: existingSession?.requestId,
        existingTaskId: existingSession?.taskId,
      })
    },
  })
}

/**
 * Hook to clear the LiveChat session and start fresh.
 * This clears sessionStorage and invalidates the query cache.
 */
export const useClearLiveChatSession = () => {
  const queryClient = useQueryClient()

  return useCallback(() => {
    // Clear session storage
    clearLiveChatSessionData()
    // Invalidate the query to force a fresh fetch
    queryClient.invalidateQueries({ queryKey: ['live-chat-schema'] })
    // Also reset any start session mutations
    queryClient.resetQueries({ queryKey: ['live-chat-start'] })
  }, [queryClient])
}
export const useChatMessages = () => {
  const { data: messages = [] } = useSuspenseQuery({
    queryKey: ['chat-messages'],
    queryFn: fetchChatHistory,
    staleTime: Infinity, // Messages don't go stale
  })

  // Sync messages to session storage whenever they change
  useEffect(() => {
    saveMessagesToStorage(messages)
  }, [messages])

  return messages
}

export const useSendMessage = () => {
  const queryClient = useQueryClient()
  const { appKey, projectId } = useAppContext()
  // const handlerId = '/showcase/chat/event'
  const handlerId = sessionStorage.getItem(CHAT_HANDLER_ID_STORAGE_KEY) || '/showcase/chat/event'

  return useMutation({
    mutationKey: ['chat-send'],
    retry: false,
    mutationFn: (request: ChatRequest) => sendChatMessage(handlerId, appKey, projectId, request),
    onMutate: async (request: ChatRequest) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['chat-messages'] })

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData<ChatMessage[]>(['chat-messages']) || []

      // Create user message
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: request.message,
        timestamp: new Date().toISOString(),
      }

      // Optimistically update to the new value
      queryClient.setQueryData<ChatMessage[]>(['chat-messages'], (old) => [...(old || []), userMessage])

      // Return a context object with the snapshotted value
      return { previousMessages, userMessage }
    },
    onSuccess: (response: ChatResponse, _variables, _context) => {
      // Add the assistant's response
      const assistantMessage: ChatMessage = {
        id: response.id,
        role: 'assistant',
        content: response.message,
        timestamp: response.timestamp,
      }

      queryClient.setQueryData<ChatMessage[]>(['chat-messages'], (old) => [...(old || []), assistantMessage])
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousMessages) {
        queryClient.setQueryData(['chat-messages'], context.previousMessages)
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['chat-messages'] })
    },
  })
}

// Clear conversation hook
export const useClearChat = () => {
  const queryClient = useQueryClient()

  return useCallback(() => {
    // Clear the React Query cache
    queryClient.setQueryData<ChatMessage[]>(['chat-messages'], [])

    // Clear session storage
    try {
      sessionStorage.removeItem(CHAT_MESSAGES_STORAGE_KEY)
    } catch (error) {
      console.warn('Failed to clear session storage:', error)
    }

    // Invalidate queries to ensure fresh state
    queryClient.invalidateQueries({ queryKey: ['chat-messages'] })
  }, [queryClient])
}

// Utility hook for easy chat management
export const useChat = () => {
  const messages = useChatMessages()
  const { mutate, error } = useSendMessage()
  const clearChat = useClearChat()
  const isMutating = useIsMutating({ mutationKey: ['chat-send'] }) > 0
  const mutationSnapshots = useMutationState({
    filters: { mutationKey: ['chat-send'] },
    select: (mutation) => ({
      status: mutation.state.status as 'idle' | 'pending' | 'error' | 'success',
      error: mutation.state.error as unknown,
      submittedAt: (mutation.state as unknown as { submittedAt?: number }).submittedAt ?? 0,
    }),
  })

  const latestMutation = useMemo(() => {
    return mutationSnapshots.reduce<
      { status: 'idle' | 'pending' | 'error' | 'success'; error: unknown; submittedAt: number } | undefined
    >((latest, current) => {
      if (!latest) return current
      return current.submittedAt > latest.submittedAt ? current : latest
    }, undefined)
  }, [mutationSnapshots])

  const sharedError = latestMutation?.status === 'error' ? (latestMutation.error as Error | null) : null

  useEffect(() => {
    console.log('isMutating', isMutating)
  }, [isMutating])

  const handleSendMessage = useCallback(
    (messageContent: string) => {
      const request: ChatRequest = {
        message: messageContent,
        history: messages,
      }

      return mutate(request)
    },
    [messages, mutate]
  )

  return useMemo(
    () => ({
      messages,
      sendMessage: handleSendMessage,
      clearChat,
      isLoading: isMutating,
      error: sharedError ?? error,
    }),
    [messages, handleSendMessage, isMutating, sharedError, error, clearChat]
  )
}
